import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { CountUp, useInView, usePrefersReducedMotion } from "./motion";

/*
 * Opsane Score - the headline compliance feature.
 *
 * Facts per opsanio/documentation opsane-score.md: 0-100, recomputed on every
 * scan, decays when hygiene slips; bands Excellent 80-100, Acceptable 60-79,
 * Degraded 40-59, Critical 0-39; 100 ("Godlike IT") is deliberately
 * unreachable. NIS2 / ISO 27001 / BSI / CIS are lenses on the same OCF
 * controls. The homepage keeps "49 OCF controls" (product-showcase copy).
 * Values are examples: mostly green with some room to improve, never near 100.
 */

const tp = "anketa.autonubil.home.score";

const SCORE = 82;
const LENSES: { key: string; label: string; value: number }[] = [
  { key: "nis2", label: "NIS2", value: 84 },
  { key: "iso", label: "ISO/IEC 27001", value: 81 },
  { key: "bsi", label: "BSI IT-Grundschutz", value: 76 },
  { key: "cis", label: "CIS Controls v8", value: 68 },
];
const BANDS = [
  { key: "critical", fb: "Critical", range: "0-39", span: 40, color: "var(--ak-sev-critical, #ff5d6c)" },
  { key: "degraded", fb: "Degraded", range: "40-59", span: 20, color: "var(--ak-sev-warning, #ffb454)" },
  { key: "acceptable", fb: "Acceptable", range: "60-79", span: 20, color: "#e8c547" },
  { key: "excellent", fb: "Excellent", range: "80-100", span: 20, color: "var(--ak-sev-ok, #4fd6a0)" },
];
const bandColor = (v: number) => (v >= 80 ? BANDS[3].color : v >= 60 ? BANDS[2].color : v >= 40 ? BANDS[1].color : BANDS[0].color);

// Progress ring that fills once it scrolls into view (static under reduced motion).
const Ring: React.FC<{ value: number; size: number; stroke: number; children?: React.ReactNode }> = ({ value, size, stroke, children }) => {
  const [ref, visible] = useInView<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const shown = visible || reduced ? value : 0;
  return (
    <div ref={ref} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ak-border)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={bandColor(value)}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown / 100)}
          style={{ transition: reduced ? undefined : "stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>{children}</div>
    </div>
  );
};

export const AutonubilOpsaneScore: React.FC = () => {
  const { translate } = useAnketaI18n();
  const band = (k: string, fb: string) => translate(`${tp}.band.${k}`, { defaultMessage: fb } as any);

  return (
    <section id="opsane-score" style={{ background: "var(--ak-page-bg)", padding: "clamp(64px, 9vw, 100px) clamp(24px, 5vw, 48px)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ maxWidth: 760, marginBottom: 40 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>
            {translate(`${tp}.eyebrow`, { defaultMessage: "Opsane Score" } as any)}
          </p>
          <h2 style={{ margin: "12px 0 0", fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1, color: "var(--ak-text)" }}>
            {translate(`${tp}.title.pre`, { defaultMessage: "One score." } as any)}{" "}
            <span style={{ background: "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", paddingBottom: "0.1em" }}>
              {translate(`${tp}.title.highlight`, { defaultMessage: "Every framework as a lens." } as any)}
            </span>
          </h2>
          <p style={{ margin: "16px 0 0", fontSize: "1.05rem", lineHeight: 1.65, color: "var(--ak-text-secondary)" }}>
            {translate(`${tp}.subtitle`, {
              defaultMessage:
                "The Opsane Score condenses 49 OCF controls into one value from 0 to 100 - recomputed on every scan. NIS2, ISO 27001, BSI IT-Grundschutz and CIS Controls look at the same data.",
            } as any)}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", gap: 24, alignItems: "stretch" }}>
          {/* score card */}
          <div style={{ background: "var(--ak-surface)", border: "1px solid var(--ak-border)", borderRadius: 16, padding: "24px 26px 26px", boxShadow: "0 1px 4px var(--ak-shadow)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>Opsane Score</span>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, color: "var(--ak-sev-ok, #4fd6a0)", border: "1px solid var(--ak-sev-ok, #4fd6a0)" }}>
                {band("excellent", "Excellent")}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
              <Ring value={SCORE} size={200} stroke={16}>
                <CountUp value={String(SCORE)} style={{ fontSize: "3.6rem", fontWeight: 800, lineHeight: 1, color: "var(--ak-text)", fontVariantNumeric: "tabular-nums" }} />
                <span style={{ marginTop: 6, fontSize: "0.85rem", color: "var(--ak-text-muted)" }}>{translate(`${tp}.of100`, { defaultMessage: "of 100" } as any)}</span>
              </Ring>
            </div>
            {/* band scale with marker */}
            <div style={{ position: "relative", display: "flex", gap: 4, height: 8, marginTop: 24 }} aria-hidden="true">
              {BANDS.map((b) => (
                <span key={b.key} style={{ flex: b.span, borderRadius: 4, background: b.color, opacity: 0.85 }} />
              ))}
              <span style={{ position: "absolute", top: -6, left: `calc(${SCORE}% - 2px)`, width: 4, height: 20, borderRadius: 2, background: "var(--ak-text)" }} />
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 10 }}>
              {BANDS.map((b) => (
                <span key={b.key} style={{ flex: b.span, fontSize: "0.78rem", lineHeight: 1.35, color: "var(--ak-text-secondary)" }}>
                  {band(b.key, b.fb)}
                  <br />
                  <span style={{ color: "var(--ak-text-muted)", fontVariantNumeric: "tabular-nums" }}>{b.range}</span>
                </span>
              ))}
            </div>
          </div>

          {/* framework lenses */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
              {LENSES.map((l) => (
                <div key={l.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <Ring value={l.value} size={112} stroke={10}>
                    <CountUp value={String(l.value)} style={{ fontSize: "1.6rem", fontWeight: 800, color: "var(--ak-text)", fontVariantNumeric: "tabular-nums" }} />
                  </Ring>
                  <span style={{ fontSize: "0.9rem", fontWeight: 700, textAlign: "center", color: "var(--ak-text)" }}>{l.label}</span>
                </div>
              ))}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "1.05rem", lineHeight: 1.55, color: "var(--ak-text)" }}>
                {translate(`${tp}.decay`, { defaultMessage: "Drops automatically as soon as hygiene slips." } as any)}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1rem", lineHeight: 1.55, color: "var(--ak-text-secondary)" }}>
                {translate(`${tp}.godlike`, { defaultMessage: "100 is “Godlike IT” - deliberately unreachable. Excellent starts at 80." } as any)}
              </p>
              <p style={{ margin: "14px 0 0", fontSize: "0.8rem", letterSpacing: "0.04em", color: "var(--ak-text-muted)" }}>
                {translate(`${tp}.note`, { defaultMessage: "Example values for illustration." } as any)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutonubilOpsaneScore;
